import React, { useEffect, useRef, useState } from 'react';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { AppBar, Box, Button, createTheme, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useApi } from '../../../../services/HttpService';
import ScrollableDataGrid from '../../../../components/tables/ScrollableDataGrid';
import { GridApiCommunity } from '@mui/x-data-grid/internals';
import BaseModal from '../../../../components/modals/BaseModal';
import { newUser, User } from '../../../../model/User';
import { StickyAlert } from '../../../../components/ui/StickyAlert';
import { highlightedGridColumns } from '../../../../components/tables/BaseTable';
import { EnhancedTableToolbar } from '../../../../components/tables/AdminTable';
import HardDeleteDialog from '../../../../components/modals/dialog/HardDeleteDialog';
import { UserOverview } from './UserOverview';

const mainColumns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'username', headerName: 'Brukernavn', width: 130 },
  { field: 'first_name', headerName: 'Fornavn', width: 130 },
  { field: 'last_name', headerName: 'Etternavn', width: 130 },
  { field: 'name', headerName: 'Fullt navn', width: 160 },
  { field: 'emailaddress', headerName: 'e-post', width: 250 },
  { field: 'telephone', headerName: 'Telefon', width: 150 },
  { field: 'accessgroups', headerName: 'Roller', width: 200 },
  {
    field: 'companies',
    headerName: 'Bedrifter',
    width: 250,
    // valueGetter: (_, row) => {
    //   const ucs = row.user_companies || [];
    //   // const filterIds = new Set(ucs.map(item => item.company_id));
    //   // const uCompanies = companies.filter((company) => filterIds.has(company.id));
    //   return ucs ? ucs.map(company => company.company_name).join(', ') : '';
    // },
  },
  { field: 'organizations', headerName: 'Organisasjoner', width: 250 }
  // { field: 'oidc_id', headerName: 'oidc ID', width: 130 },
];

export const ManageUsers = () => {
  const theme = useTheme();
  const { api, isLoading, error } = useApi();
  const { api: deleteApi, isLoading: deleteIsLoading, error: deleteError } = useApi();
  const { api: dataApi, error: dataError } = useApi();
  const [items, setItems] = useState<User[]>([]);
  const [item, setItem] = useState<User>(newUser);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState<readonly GridRowId[]>([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [openEditUser, setOpenEditUser] = useState(false);
  const apiRef = useRef<GridApiCommunity>(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    getUsers();
    getData();
  }, []);

  const getUsers = () => {
    const body = { 
      table_name: "oidc_user", 
      search: searchText, 
      limit: 0,
      offset: 0,
    }

    api("/manage/tableOverviewQuery", "POST", body).then((res) => {
      if (res?.statusCode === 200) {
        setItems(res.data);
      }
    });
  }

  const getData = () => {
  }

  useEffect(() => {
    handleSearch(searchText);
  }, [items]);

  // useEffect(() => {
  //   getUsers();
  // }, [searchText]);

  const handleSearch = (searchText: string) => {
    const query = searchText.toLowerCase();
    setSearchText(query);

    const filteredData = items ? items.filter((row) =>
      mainColumns.some((column) => {

        // Use valueGetter if defined, otherwise use the field directly
        const value = column.valueGetter
          ? column.valueGetter(undefined as never, row, column, apiRef)
          : row[column.field];

        return String(value).toLowerCase().includes(query);
      })
    ) : [];

    setFilteredUsers(filteredData);
  };

  const getPageUsers = (page: number, pageSize: number) => {
    if (items.length === 0) {
      return [];
    }
    const sublist = items.slice(page * pageSize, (page + 1) * pageSize);
    console.log("returning users from index", page * pageSize, "-", (page + 1) * pageSize, "count", sublist.length)
    return sublist;
  }

  const onUserChange = (changedUser: User) => {
    setItems((prevUsers) => {
      const index = prevUsers.findIndex(u => u.id === changedUser.id);
      if (index > -1) {
        return prevUsers.map((user, i) => (i === index ? changedUser : user));
      } else {
        return [...prevUsers, changedUser];
      }
    });

    setItem(changedUser);
    setSelectedIds([changedUser.id]);
  }

  const handleRowSelectionModelChange = (ids: number[]) => {
    setSelectedIds(ids);

    if (ids.length > 0) {
      let selectedUser = items.find(u => u.id === ids[0]);
      console.log("found user:", selectedUser)
      setItem(selectedUser);
    } else {
      setItem(newUser());
    }
  }

  const handleRowDoubleClick = (row) => {
    let ids = [row.id];
    handleRowSelectionModelChange(ids);
    setOpenEditUser(true);
  }

  const handleDelete = (hardDelete: boolean) => {
    alert("delete not implemented");
  }

  return (
    <>
      <BaseModal
        open={openEditUser}
        title={"Bruker"}
        onClose={() => setOpenEditUser(false)}
        closeOnBackdropClick={false}
        showButtons={false}
      >
        <UserOverview item={item} onClose={() => setOpenEditUser(false) } onItemChange={onUserChange} />
      </BaseModal>
      <HardDeleteDialog
        open={openDelete}
        bodyText={`Er du sikker på at du vil slette ${item.first_name} ${item.last_name}? Trykk på "SLETT" for å bekrefte.`}
        includeHardDelete={true}
        loading={deleteIsLoading}
        errorMessage={deleteError}
        onClose={() => setOpenDelete(false)}
        onConfirm={(hardDelete) => handleDelete(hardDelete)}
      />

      <StickyAlert message={error ? error : dataError ? dataError : null} severity="error" />

      <Box sx={{ m: 1, mt: 3, p: { xs: 1, md: 3 }, pt: 0, width: "100%" }}>
        <Paper elevation={3} sx={{ borderRadius: "4px 4px 0 0" }}>
          <EnhancedTableToolbar
            title={"Brukere"}
            numSelected={openEditUser || selectedIds.length > 0 ? 1 : 0}
            selectTitle={item.id ? `${item.first_name} ${item.last_name} (${item.username})` : "Ny bruker"}
            onAdd={() => { setOpenEditUser(true); setSelectedIds([]); setItem(newUser()) }}
            onEdit={() => setOpenEditUser(true)}
            onCancel={() => { setOpenEditUser(false); setSelectedIds([]); setItem(newUser()) }}
            onDelete={() => setOpenDelete(true)}
            onSearch={handleSearch}
            onRefresh={getUsers}
          />
        </Paper>
        {/* {openEditUser ?
          <>
            <UserOverview user={user} onClose={() => setOpenEditUser(false)} />
          </> : */}
        <ScrollableDataGrid
          height={700}
          rows={filteredUsers}
          columns={mainColumns ? highlightedGridColumns(mainColumns, searchText, apiRef) : []}
          loading={isLoading}
          onPageRequest={getPageUsers}
          checkboxSelection
          disableMultipleRowSelection
          onRowSelectionModelChange={(ids) => handleRowSelectionModelChange(ids)}
          onRowDoubleClick={(row) => handleRowDoubleClick(row)}
          rowSelectionModel={selectedIds}
          keepNonExistentRowsSelected={true}
        />
        {/* } */}
      </Box>

      {/* THIS MIGHT BE A SOLUTION TO INFINITE SCROLLING  */}
      {/* <Box sx={{ m: 1, p: 3, height: 700, pt: 0, width: "100%" }}>
        <VirtualizedDataGrid
          rows={filteredUsers}
          columns={mainColumns ? highlightedGridColumns(mainColumns, searchText, apiRef) : []}
          loading={isLoading}
          theme={theme}
        />
      </Box> */}
    </>
  )
}